// Push-notification diagnostics.
//
// Built because "FCM accepted the message" and "the phone showed a
// notification" are entirely different claims, and for a while nothing in this
// system could tell them apart: Android was returning a valid token, FCM was
// returning success, and no notification ever appeared. Each function here
// isolates one link in the chain so a failure can be attributed rather than
// guessed at.
//
// The chain, in order:
//   1. OS/browser will display a notification at all   → sendLocalTestNotification()
//   2. A push subscription exists and points somewhere → collectPushDiagnostics()
//   3. Pushes actually arrive on the device            → readPushLog()
//   4. FCM accepts the send                            → the Worker's debug endpoint

const PUSH_LOG_DB = 'aspire_push_log';
const PUSH_LOG_STORE = 'events';

// Must match the scope firebase-messaging-sw.js is registered at in
// notifications/fcm.js — the push subscription belongs to THAT registration,
// not to the app's Workbox service worker.
const FCM_SCOPE_SUFFIX = '/firebase-cloud-messaging-push-scope';

function openPushLog() {
  return new Promise((resolve, reject) => {
    // Same version the service worker creates. Opening with a lower version
    // would throw; opening with a higher one would trigger an upgrade the SW
    // doesn't expect.
    const req = indexedDB.open(PUSH_LOG_DB, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(PUSH_LOG_STORE)) {
        db.createObjectStore(PUSH_LOG_STORE, { keyPath: 'id', autoIncrement: true });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Everything the service worker has seen arrive, newest last. */
export async function readPushLog() {
  try {
    const db = await openPushLog();
    const events = await new Promise((resolve, reject) => {
      const tx = db.transaction(PUSH_LOG_STORE, 'readonly');
      const req = tx.objectStore(PUSH_LOG_STORE).getAll();
      req.onsuccess = () => resolve(req.result ?? []);
      req.onerror = () => reject(req.error);
    });
    db.close();
    return events;
  } catch (e) {
    return [];
  }
}

export async function clearPushLog() {
  try {
    const db = await openPushLog();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(PUSH_LOG_STORE, 'readwrite');
      tx.objectStore(PUSH_LOG_STORE).clear();
      tx.oncomplete = resolve;
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch (e) { /* non-fatal */ }
}

/**
 * Show a notification directly, with no network and no FCM involved.
 *
 * This is the most valuable single test in the file. If this doesn't appear on
 * the device, then nothing about the server, the token, or the payload matters
 * — the OS or browser is refusing to display notifications for this app, and
 * that's a settings problem (Android per-app notification permission, or a
 * manufacturer battery/background restriction) that no amount of code will fix.
 */
export async function sendLocalTestNotification() {
  if (typeof Notification === 'undefined') {
    return { ok: false, reason: 'This browser has no Notification API.' };
  }
  if (Notification.permission !== 'granted') {
    return { ok: false, reason: `Notification permission is "${Notification.permission}", not "granted".` };
  }
  if (!('serviceWorker' in navigator)) {
    return { ok: false, reason: 'No service worker support.' };
  }

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    const fcmReg = registrations.find((r) => r.scope.endsWith(FCM_SCOPE_SUFFIX));
    const reg = fcmReg ?? (await navigator.serviceWorker.ready);
    await reg.showNotification('ASPIRE local test', {
      body: 'Shown by the app itself — no server involved.',
      icon: '/icons/icon-192.png',
      tag: 'aspire-local-test',
      renotify: true,
    });
    return {
      ok: true,
      reason: `Asked ${fcmReg ? 'the FCM' : 'the app'} service worker to display it.`,
    };
  } catch (err) {
    return { ok: false, reason: String(err) };
  }
}

/** A snapshot of the local half of the push chain. */
export async function collectPushDiagnostics() {
  const out = {
    permission: typeof Notification === 'undefined' ? 'unsupported' : Notification.permission,
    standalone:
      window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone || false,
    registrations: [],
    subscription: null,
  };

  if (!('serviceWorker' in navigator)) return out;

  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    for (const r of registrations) {
      let subscription = null;
      try {
        const sub = await r.pushManager.getSubscription();
        if (sub) {
          // Only the host, never the full endpoint — that's a capability URL
          // anyone could send pushes to.
          subscription = new URL(sub.endpoint).host;
          out.subscription = subscription;
        }
      } catch (e) { /* some registrations have no push manager */ }

      out.registrations.push({
        scope: new URL(r.scope).pathname,
        script: r.active?.scriptURL ? new URL(r.active.scriptURL).pathname : '(none active)',
        subscription,
      });
    }
  } catch (e) { /* best effort */ }

  return out;
}

/**
 * Ask the browser to check for a new build right now.
 *
 * The app already updates itself on launch, but "am I running the latest
 * version?" was previously only answerable by reading a timestamp and knowing
 * what to compare it to.
 */
export async function checkForUpdate() {
  if (!('serviceWorker' in navigator)) return { ok: false, state: 'unsupported' };
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    // The app shell is served by the Workbox worker at the root scope; the FCM
    // worker has its own scope and never changes the app's version.
    const appReg =
      registrations.find((r) => !r.scope.endsWith(FCM_SCOPE_SUFFIX)) ?? registrations[0];
    if (!appReg) return { ok: false, state: 'no-registration' };

    await appReg.update();
    if (appReg.installing || appReg.waiting) {
      return { ok: true, state: 'update-found' };
    }
    return { ok: true, state: 'up-to-date' };
  } catch (err) {
    return { ok: false, state: String(err) };
  }
}
